<div class="job-map-page">
  <section class="job-map-page__filters">
    <mat-form-field appearance="outline" class="filter--address">
      <mat-label>{{ 'JOB_MAP.CANDIDATE_LOCATION' | translate }}</mat-label>
      <input matInput [formControl]="addressControl" placeholder="Vilnius, LT" />
      <mat-icon matSuffix>place</mat-icon>
    </mat-form-field>

    <div class="filter--distance">
      <div class="filter--distance__head">
        <span>{{ 'JOB_MAP.DISTANCE' | translate }}</span>
        <span class="filter--distance__value">{{ radiusKm }} km</span>
      </div>
      <mat-slider
        class="filter--distance__slider"
        [min]="radiusMin"
        [max]="radiusMax"
        [step]="1"
        [value]="radiusKm"
        (input)="onRadiusChange($event.value)"
      ></mat-slider>
    </div>

    <mat-form-field appearance="outline" class="filter--city">
      <mat-label>{{ 'JOB_MAP.CITY' | translate }}</mat-label>
      <mat-select
        [formControl]="cityControl"
        multiple
        [placeholder]="'JOB_MAP.ALL_CITIES' | translate"
        (selectionChange)="onFilterChange()"
      >
        <mat-option>
          <ngx-mat-select-search
            [formControl]="citySearchControl"
            [placeholderLabel]="'JOB_MAP.CITY_SEARCH' | translate"
            [noEntriesFoundLabel]="'JOB_MAP.NO_CITY_FOUND' | translate"
          ></ngx-mat-select-search>
        </mat-option>
        <button
          mat-button
          type="button"
          class="select-all-button"
          *ngIf="filteredCityOptions.length > 0"
          (click)="toggleAllCities()"
        >
          {{ areAllCitiesSelected()
            ? ('JOB_MAP.DESELECT_ALL' | translate)
            : ('JOB_MAP.SELECT_ALL' | translate) }}
        </button>
        <mat-option
          *ngFor="let city of filteredCityOptions; trackBy: trackCityById"
          [value]="city.id"
        >
          {{ city.title }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter--type">
      <mat-label>{{ 'JOB_MAP.JOB_TYPE' | translate }}</mat-label>
      <mat-select
        [formControl]="jobTypeControl"
        multiple
        [placeholder]="'JOB_MAP.ALL_TYPES' | translate"
        (selectionChange)="onFilterChange()"
      >
        <button
          mat-button
          type="button"
          class="select-all-button"
          *ngIf="jobTypeOptions.length > 0"
          (click)="toggleAllJobTypes()"
        >
          {{ areAllJobTypesSelected()
            ? ('JOB_MAP.DESELECT_ALL' | translate)
            : ('JOB_MAP.SELECT_ALL' | translate) }}
        </button>
        <mat-option
          *ngFor="let opt of jobTypeOptions; trackBy: trackOrderTypeByValue"
          [value]="opt.value"
        >
          {{ opt.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <button mat-stroked-button class="reset-button" (click)="resetFilters()">
      <mat-icon>refresh</mat-icon>
      {{ 'JOB_MAP.RESET' | translate }}
    </button>
  </section>

  <section class="job-map-page__body">
    <div class="job-map-page__map">
      <ng-container *ngIf="mapsReady; else mapsUnavailable">
        <google-map
          height="100%"
          width="100%"
          [center]="center"
          [zoom]="zoom"
          [options]="mapOptions"
        >
          <map-circle
            *ngIf="hasResolvedLocation"
            [center]="center"
            [radius]="radiusMeters"
            [options]="radiusCircleOptions"
          ></map-circle>
        </google-map>
      </ng-container>
      <ng-template #mapsUnavailable>
        <div class="map-unavailable">
          <mat-icon>map</mat-icon>
          <p>{{ 'JOB_MAP.MAPS_UNAVAILABLE' | translate }}</p>
        </div>
      </ng-template>
    </div>

    <aside class="job-map-page__panel">
      <header class="panel-header">
        <h3>
          {{ 'JOB_MAP.NEARBY' | translate }}
          <span class="count" *ngIf="!loading">({{ visiblePins.length }})</span>
        </h3>
      </header>

      <div *ngIf="loading" class="panel-empty">{{ 'JOB_MAP.LOADING' | translate }}</div>
      <div *ngIf="!loading && resolveError === 'NO_RESULT'" class="panel-empty">
        {{ 'JOB_MAP.ADDRESS_NOT_FOUND' | translate }}
      </div>
      <div *ngIf="!loading && !resolveError && visiblePins.length === 0" class="panel-empty">
        {{ 'JOB_MAP.NO_NEARBY' | translate }}
      </div>

      <ul class="pin-list">
        <li
          *ngFor="let pin of visiblePins; trackBy: trackPinById"
          class="pin-card"
          [class.pin-card--active]="openInfoPin?.id === pin.id"
          (click)="focusPin(pin)"
        >
          <div class="pin-card__title">{{ pin.positionTitle || '—' }}</div>
          <div class="pin-card__company" *ngIf="pin.companyName">{{ pin.companyName }}</div>

          <div class="pin-card__chips" *ngIf="pin.id || pin.status || pin.orderType">
            <a
              *ngIf="pin.id"
              class="pin-card__chip pin-card__chip--link"
              [href]="fvUrlForOrder(pin.id)"
              target="_blank"
              rel="noopener"
              (click)="$event.stopPropagation()"
            >
              #{{ pin.orderId }}
              <mat-icon inline="true">open_in_new</mat-icon>
            </a>
            <span
              *ngIf="pin.status"
              class="pin-card__chip pin-card__chip--status"
              [class]="'pin-card__chip pin-card__chip--status pin-card__chip--' + pin.status"
            >{{ pin.status }}</span>
            <span *ngIf="pin.orderType" class="pin-card__chip pin-card__chip--type">
              {{ formatOrderType(pin.orderType) }}
            </span>
          </div>

          <div class="pin-card__row" *ngIf="pin.address">
            <mat-icon inline="true">place</mat-icon>
            <span>{{ pin.address }}</span>
          </div>
          <div class="pin-card__row" *ngIf="pin.distanceKm !== null">
            <mat-icon inline="true">straighten</mat-icon>
            <span>{{ pin.distanceKm | number: '1.1-1' }} km {{ 'JOB_MAP.FROM_CANDIDATE' | translate }}</span>
          </div>
          <div class="pin-card__row" *ngIf="pin.recruiterName">
            <mat-icon inline="true">person</mat-icon>
            <span>{{ pin.recruiterName }}</span>
          </div>
        </li>
      </ul>
    </aside>
  </section>
</div>
